import type { TokenCredential } from "@azure/identity";
import { deployPromptAdvisor } from "./deploy-prompt-advisor.js";

export const SIMPLE_STACEY_PERSONA = "startup-advisor-intake";
export const SIMPLE_STACEY_AGENT = "stacey-intake";

/**
 * Deploy the no-brain Simple Stacey (`stacey-intake`) from the intake persona.
 * Reads model + reasoning from the persona frontmatter; the body (placeholder-
 * free) becomes the instructions. ~10s via createPromptVersion. Idempotent in
 * the Foundry sense (each call = a new version).
 *
 * Delegates to `deployPromptAdvisor` — the intake persona has no fillable-fields
 * so the defaults map is empty and rendering is unchanged.
 */
export async function deploySimpleStacey(args: {
  credential: TokenCredential;
  endpoint: string;
  repoRoot: string;
}): Promise<string> {
  try {
    return await deployPromptAdvisor({
      credential: args.credential,
      endpoint: args.endpoint,
      repoRoot: args.repoRoot,
      persona: SIMPLE_STACEY_PERSONA,
      agentName: SIMPLE_STACEY_AGENT,
    });
  } catch (e: unknown) {
    // Re-map ADVISOR_* codes back to SIMPLE_STACEY_* codes so existing callers
    // (and the existing test suite) observe unchanged error codes.
    if (e && typeof e === "object" && "code" in e) {
      const err = e as { code: string; message: string; hint?: string; cause?: unknown };
      if (err.code === "ADVISOR_PERSONA_MISSING") {
        const { LocalCliError } = await import("./errors.js");
        throw new LocalCliError({
          code: "SIMPLE_STACEY_PERSONA_MISSING",
          message: err.message,
          hint: err.hint,
          cause: err.cause,
        });
      }
      if (err.code === "ADVISOR_NO_MODEL") {
        const { LocalCliError } = await import("./errors.js");
        throw new LocalCliError({
          code: "SIMPLE_STACEY_NO_MODEL",
          message: err.message,
          hint: err.hint,
          cause: err.cause,
        });
      }
      if (err.code === "ADVISOR_BAD_EFFORT") {
        const { LocalCliError } = await import("./errors.js");
        throw new LocalCliError({
          code: "SIMPLE_STACEY_BAD_EFFORT",
          message: err.message,
          hint: err.hint,
          cause: err.cause,
        });
      }
    }
    throw e;
  }
}
